// src/pex/diff.ts
function computeDiff(pd, vp) {
  const warnings = [];
  const credentials = vp?.verifiableCredential ?? [];
  const perDescriptor = (pd.input_descriptors ?? []).map(
    (desc, idx) => computeDescriptorDiff(desc, credentials, idx)
  );
  const totalAsked = perDescriptor.reduce((s, d) => s + d.askedFor.length, 0);
  const totalRevealed = perDescriptor.reduce((s, d) => s + d.willReveal.length, 0);
  const totalProvenWithoutRevealing = perDescriptor.reduce(
    (s, d) => s + d.willProvePredicate.length,
    0
  );
  return { perDescriptor, totalAsked, totalRevealed, totalProvenWithoutRevealing, warnings };
}
function computeDescriptorDiff(desc, credentials, descriptorIndex = 0) {
  const fields = (desc.constraints?.fields ?? []).map((f) => fieldToRequest(f));
  const askedFor = fields.map((f) => f.path);
  const candidate = pickCandidate(desc, credentials, descriptorIndex);
  const willReveal = [];
  const willHide = [];
  const willProvePredicate = [];
  const warnings = [];
  if (candidate) {
    for (const field of fields) {
      if (field.predicate === "required" || field.predicate === "preferred") {
        willProvePredicate.push({
          fieldName: leafName(field.path),
          op: extractFilterOp(field) ?? "unknown",
          threshold: extractFilterThreshold(field),
          path: field.path
        });
        continue;
      }
      let resolved;
      for (const path of field.paths) {
        const value = evalPath(path, candidate);
        if (value !== void 0) {
          resolved = { value, path };
          break;
        }
      }
      if (resolved) {
        willReveal.push(leafName(resolved.path));
      } else if (!field.optional) {
        warnings.push(`required field "${leafName(field.path)}" not present on candidate credential`);
      }
    }
    const isBBS = candidate.proof?.type === "BbsBlsSignature2020" || desc.constraints?.limit_disclosure === "required";
    if (isBBS) {
      const subject = candidate.credentialSubject ?? {};
      const askedLeaves = new Set(willReveal);
      for (const k of Object.keys(subject)) {
        if (k === "id") continue;
        if (!askedLeaves.has(k)) willHide.push(k);
      }
    }
  }
  return {
    descriptorId: desc.id,
    descriptorName: desc.name,
    descriptorPurpose: desc.purpose,
    askedFor,
    willReveal,
    willHide,
    willProvePredicate,
    candidateCredentialId: candidate?.id,
    fields,
    warnings
  };
}
function fieldToRequest(f) {
  const paths = (f.path ?? []).filter(Boolean);
  return {
    path: paths[0] ?? "",
    paths,
    predicate: f.predicate,
    purpose: f.purpose,
    optional: !!f.optional,
    fieldId: f.id
  };
}
function pickCandidate(desc, credentials, fallbackIdx) {
  if (credentials.length === 0) return void 0;
  const pinned = desc.constraints?.schema_id;
  if (pinned) {
    const match = credentials.find((vc) => vc.credentialSchema?.id === pinned);
    if (match) return match;
  }
  const formatKeys = Object.keys(desc.format ?? {});
  if (formatKeys.length) {
    const allowedProofTypes = /* @__PURE__ */ new Set();
    for (const k of formatKeys) {
      const fmt = desc.format?.[k];
      for (const t of fmt?.proof_type ?? []) allowedProofTypes.add(t);
    }
    if (allowedProofTypes.size > 0) {
      const match = credentials.find((vc) => vc.proof && allowedProofTypes.has(vc.proof.type));
      if (match) return match;
    }
  }
  return credentials[Math.min(fallbackIdx, credentials.length - 1)];
}
function evalPath(path, doc) {
  if (!path || path === "$") return doc;
  if (!path.startsWith("$")) return void 0;
  let i = 1;
  let cursor = doc;
  while (i < path.length) {
    const ch = path[i];
    if (cursor == null) return void 0;
    if (ch === ".") {
      if (path[i + 1] === ".") return void 0;
      let j = i + 1;
      while (j < path.length && /[A-Za-z0-9_]/.test(path[j])) j++;
      const name = path.slice(i + 1, j);
      if (!name) return void 0;
      cursor = cursor[name];
      i = j;
    } else if (ch === "[") {
      const close = path.indexOf("]", i);
      if (close < 0) return void 0;
      const inner = path.slice(i + 1, close).trim();
      i = close + 1;
      if (inner === "*") {
        if (!Array.isArray(cursor)) return void 0;
        const remainder = path.slice(i);
        for (const el of cursor) {
          const v = remainder ? evalPath("$" + remainder, el) : el;
          if (v !== void 0) return v;
        }
        return void 0;
      }
      const idx = Number(inner);
      if (Number.isFinite(idx)) {
        if (!Array.isArray(cursor)) return void 0;
        cursor = cursor[idx];
        continue;
      }
      const m = inner.match(/^['"]([^'"]+)['"]$/);
      if (m) {
        cursor = cursor[m[1]];
        continue;
      }
      return void 0;
    } else {
      return void 0;
    }
  }
  return cursor;
}
function leafName(path) {
  if (!path) return "";
  let i = path.length - 1;
  while (i >= 0 && path[i] === "]") {
    const open = path.lastIndexOf("[", i);
    if (open < 0) break;
    i = open - 1;
  }
  if (i < 0) return path;
  let j = i;
  while (j >= 0 && /[A-Za-z0-9_]/.test(path[j])) j--;
  return path.slice(j + 1, i + 1);
}
function extractFilterOp(field) {
  void field;
  return void 0;
}
function extractFilterThreshold(field) {
  void field;
  return void 0;
}

export {
  computeDiff,
  computeDescriptorDiff,
  evalPath
};
